// Global slide-out navigation drawer (from the left).
//
// MOBILE-FIRST. Premium OTA-style menu. Hamburger trigger lives in the Navbar.
// Sections:
//   • Header — logo + close
//   • Profile card — login CTA OR avatar/name/email + view profile link
//   • Account — My Profile · My Trips · Wishlist · Saved Travellers · Notifications
//   • Engagement — Gift Cards · Refer & Earn · My Reviews · Rewards
//   • Support — Customer Support · Help Centre
//   • Future — Corporate Dashboard · Wallet/Credits (visible only when logged-in & corporate / always shown with "soon" badge)
//   • Footer — Theme toggle · Logout (when logged in)
//
// Body-scroll lock + backdrop click-to-close + swipe-left to close on touch devices.
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  X, User, Briefcase, Heart, Bell, Gift, Star, Users, Headphones, HelpCircle, LogIn,
  LogOut, Sun, Moon, Building2, Wallet, ArrowRight, Sparkles, FileText, Plane,
} from 'lucide-react';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useTheme } from '../../context/ThemeContext';

const SECTION_HEADER = 'px-4 pt-4 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 dark:text-gray-500';

const MenuItem = ({ Icon, label, to, onClick, badge, soon, testId }) => {
  const inner = (
    <>
      <span className="grid place-items-center h-9 w-9 rounded-lg bg-gray-50 dark:bg-gray-800 group-hover:bg-red-50 dark:group-hover:bg-red-950/40 transition-colors">
        <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400 group-hover:text-red-600" />
      </span>
      <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">{label}</span>
      {badge && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white">{badge}</span>}
      {soon && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">SOON</span>}
      <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-400" />
    </>
  );
  const baseCls = 'group flex items-center gap-3 w-full px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors tap-target';
  if (to) {
    return <Link to={to} onClick={onClick} className={baseCls} data-testid={testId}>{inner}</Link>;
  }
  return <button type="button" onClick={onClick} className={baseCls} data-testid={testId}>{inner}</button>;
};

const NavigationDrawer = ({ open, onClose, onOpenAuth }) => {
  const { customer, logout } = useCustomerAuth();
  const { theme, toggleTheme } = useTheme();
  const touchStartX = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Basic swipe-left-to-close on the drawer surface
  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (dx < -60) onClose();
    touchStartX.current = null;
  };

  const isCorporate = customer?.account_type === 'corporate';
  const close = () => onClose();
  const requireLogin = () => { onClose(); onOpenAuth(); };

  return (
    <div
      className={`fixed inset-0 z-[70] transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      aria-hidden={!open}
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={close} data-testid="nav-drawer-backdrop" />
      <aside
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="nav-drawer"
        className={`absolute top-0 left-0 h-full w-[88%] sm:w-[400px] max-w-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col transform transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <img src="/elite-travo-logo.png" alt="Elite Travo" className="h-8 w-8 object-contain" />
            <span className="font-bold text-gray-900 dark:text-white">Elite Travo</span>
          </div>
          <button onClick={close} aria-label="Close" data-testid="nav-drawer-close"
                  className="tap-target p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Profile card */}
        <div className="px-4 pt-4">
          {customer ? (
            <Link to="#" onClick={close} className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg" data-testid="nav-drawer-profile-card">
              {customer.avatar ? (
                <img src={customer.avatar} alt={customer.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-white/40" />
              ) : (
                <div className="h-12 w-12 rounded-full bg-white/20 grid place-items-center text-xl font-bold ring-2 ring-white/40">
                  {(customer.name || customer.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-bold truncate flex items-center gap-1.5">
                  {customer.name || 'Traveller'}
                  {isCorporate && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-300 text-amber-900">biz</span>}
                </p>
                <p className="text-[11px] text-white/80 truncate">{customer.email || customer.phone}</p>
                <p className="text-[10px] text-white/70 mt-0.5 uppercase tracking-wider">
                  {customer.loyalty?.tier || 'Bronze'} member · {customer.loyalty?.points ?? 0} pts
                </p>
              </div>
              <ArrowRight className="h-4 w-4 opacity-80" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={requireLogin}
              data-testid="nav-drawer-login"
              className="w-full flex items-center justify-between gap-3 p-3 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg hover:from-red-700 hover:to-red-800 transition-all tap-target"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-12 w-12 rounded-full bg-white/20 grid place-items-center ring-2 ring-white/40">
                  <LogIn className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-left">Login or Sign up</p>
                  <p className="text-[11px] text-white/80 text-left">Faster checkout · Booking history · Exclusive deals</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 opacity-80 shrink-0" />
            </button>
          )}
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto py-2">
          <p className={SECTION_HEADER}>Account</p>
          <MenuItem testId="nav-item-profile"     Icon={User}     label="My Profile"        onClick={customer ? close : requireLogin} />
          <MenuItem testId="nav-item-trips"       Icon={Plane}    label="My Trips"          onClick={() => { if (!customer) return requireLogin(); close(); window.location.href = '/account/bookings'; }} />
          <MenuItem testId="nav-item-wishlist"    Icon={Heart}    label="Wishlist"          onClick={() => { close(); window.location.href = '/account/wishlist'; }} />
          <MenuItem testId="nav-item-travellers"  Icon={Users}    label="Saved Travellers"  onClick={() => { if (!customer) return requireLogin(); close(); window.location.href = '/account/travellers'; }} />
          <MenuItem testId="nav-item-wallet"      Icon={Wallet}   label="My Wallet"         onClick={() => { if (!customer) return requireLogin(); close(); window.location.href = '/account/wallet'; }} />
          <MenuItem testId="nav-item-notifs"      Icon={Bell}     label="Notifications"     onClick={customer ? close : requireLogin} />

          <p className={SECTION_HEADER}>Engagement</p>
          <MenuItem testId="nav-item-gift"        Icon={Gift}     label="Gift Cards"        onClick={close} />
          <MenuItem testId="nav-item-refer"       Icon={Sparkles} label="Refer & Earn"      onClick={close} />
          <MenuItem testId="nav-item-reviews"     Icon={Star}     label="My Reviews"        onClick={customer ? close : requireLogin} />
          <MenuItem testId="nav-item-rewards"     Icon={Star}     label="Rewards & Loyalty" onClick={customer ? close : requireLogin} soon />

          {isCorporate && (
            <>
              <p className={SECTION_HEADER}>Business</p>
              <MenuItem testId="nav-item-corp"    Icon={Building2} label="Corporate Dashboard" onClick={close} soon />
              <MenuItem testId="nav-item-gst"     Icon={FileText} label="GST Invoices"         onClick={close} soon />
            </>
          )}

          <p className={SECTION_HEADER}>Wallet</p>
          <MenuItem testId="nav-item-wallet"      Icon={Wallet}   label="Wallet & Credits"  onClick={customer ? close : requireLogin} soon />

          <p className={SECTION_HEADER}>Support</p>
          <MenuItem testId="nav-item-support"     Icon={Headphones} label="Customer Support" onClick={close} />
          <MenuItem testId="nav-item-help"        Icon={HelpCircle} label="Help Centre"      onClick={close} />
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-800 p-3 flex items-center gap-2">
          <button onClick={toggleTheme} data-testid="nav-drawer-theme"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 tap-target">
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4 text-yellow-400" />}
            {theme === 'light' ? 'Dark' : 'Light'}
          </button>
          {customer && (
            <button onClick={async () => { await logout(); close(); }} data-testid="nav-drawer-logout"
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-red-50 dark:bg-red-950/30 text-sm font-semibold text-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 tap-target">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          )}
        </div>
      </aside>
    </div>
  );
};

export default NavigationDrawer;
